import { memo, useEffect, useState, useRef } from "react";

import {
  InitialsSquareComponent,
  MajorButtonComponent,
  SpinnerComponent,
} from "@zenith/components";
import { formatCurrency } from "@zenith/utils";
import {
  usePaymentLinkContext,
  useServiceFailureContext,
} from "@zenith/context/global-context/hooks";
import { NOT_NUMBERS_AND_DOT } from "@zenith/data/constants";
import { SaleItemDTO } from "@zenith/services/transactions";
import { useNavigate, useParams } from "react-router-dom";
import { PaymentLinkPublicApiService } from "@zenith/services/transactions";
import {
  PAYMENT_LINK_BANK_TRF_DETAILS,
  PAYMENT_LINK_CARD_DETAILS,
  PAYMENT_LINK_QR_CODE_DETAILS,
  PAYMENT_LINK_USSD_CODE_DETAILS,
  PAYMENT_LINK_METHOD_PAGE,
} from "@zenith/routes/paths";
import { usePaymentLinkOperationId } from "@zenith/hooks/usePaymentLinkOperationId";

const PaymentLinkPublicPage = memo(function PaymentLinkPublicPage() {
  const { paymentLinkContext, setPaymentLinkContext } = usePaymentLinkContext();
  const { setServiceFailureContext } = useServiceFailureContext();
  const hasFetchedRef = useRef(false);

  const {
    totalAmount,
    paymentLink: { businessTradingName },
    salesItems,
  } = paymentLinkContext;

  const navigate = useNavigate();
  const { operationId: paramOpId } = useParams();

  const operationId = usePaymentLinkOperationId() || paramOpId;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasCheckedPaymentMethod, setHasCheckedPaymentMethod] =
    useState<boolean>(false);

  useEffect(() => {
    setServiceFailureContext(false);
  }, [setServiceFailureContext]);

  useEffect(() => {
    if (!operationId || hasFetchedRef.current) return;

    hasFetchedRef.current = true;

    const loadPaymentLinkData = async () => {
      let hasNavigated = false;

      if (!operationId) {
        console.warn("No operationId found");
        setIsLoading(false);
        return;
      }

      if (operationId === "cfe5f4e9-3a7d-41c1-85dc-1235464ee073") {
        console.warn(
          "Detected problematic default operationId, attempting recovery"
        );

        const storedOpId =
          sessionStorage.getItem("paymentLinkCurrentOpId") ||
          localStorage.getItem("paymentLinkLastOpId");

        if (storedOpId && storedOpId !== operationId) {
          hasNavigated = true;
          navigate(`/payment-link/${storedOpId}${window.location.search}`, {
            replace: true,
          });
          return;
        }

        setIsLoading(false);
        return;
      }

      try {
        const response =
          await PaymentLinkPublicApiService.getPaymentLinkOperationById(
            operationId
          );

        if (response.success && response.data) {
          setPaymentLinkContext({
            ...paymentLinkContext,
            operationDetails: response.data,
            totalAmount: response.data.totalAmount || 0,
            paymentLink: {
              businessTradingName:
                response.data.paymentLink?.merchantTradingName ||
                "Unknown Merchant",
              expirationDate:
                response.data.paymentLink?.expirationDate ||
                new Date().toISOString(),
            },
            clientData: paymentLinkContext.clientData,
            salesItems: response.data.saleItemDetail || [],
          });

          const paymentLinkMethod =
            response.data.paymentLink?.paymentLinkMethod;
          const hasBankTransfer = response.data.inBankTransfer;

          // If payment method already exists, redirect to the appropriate details page
          if (paymentLinkMethod || hasBankTransfer) {
            const methodToUse =
              paymentLinkMethod ||
              (hasBankTransfer ? "IN_BANK_TRANSFER" : null);

            if (methodToUse) {
              let targetRoute = "";
              switch (methodToUse) {
                case "IN_BANK_TRANSFER":
                  targetRoute = PAYMENT_LINK_BANK_TRF_DETAILS;
                  break;
                case "CARD":
                  targetRoute = PAYMENT_LINK_CARD_DETAILS;
                  break;
                case "QR_CODE":
                  targetRoute = PAYMENT_LINK_QR_CODE_DETAILS;
                  break;
                case "USSD":
                  targetRoute = PAYMENT_LINK_USSD_CODE_DETAILS;
                  break;
                default:
                  return;
              }

              hasNavigated = true;
              navigate(targetRoute, {
                replace: true,
                state: {
                  operationId: operationId,
                  fromPaymentLinkPublicPage: true,
                },
              });
              return;
            }
          }

          setHasCheckedPaymentMethod(true);
        } else {
          console.error("Failed to load payment link:", response.message);
          setHasCheckedPaymentMethod(true);
        }
      } catch (error) {
        console.error("Failed to load payment link:", error);
        setHasCheckedPaymentMethod(true);
      } finally {
        if (!hasNavigated) {
          setIsLoading(false);
        }
      }
    };

    loadPaymentLinkData();
  }, [operationId, setPaymentLinkContext]);

  function getTotalItems(): number {
    let totalItems = 0;

    salesItems.forEach((item: SaleItemDTO) => {
      totalItems += Number(item.quantity);
    });

    return totalItems;
  }

  function getTotalAmount(): number {
    let sum = 0;

    salesItems.forEach((item: SaleItemDTO) => {
      if (item.amountPerUnit) {
        const amount = parseFloat(
          item.amountPerUnit?.toString().replace(NOT_NUMBERS_AND_DOT, "")
        );
        const quantity = Number(item.quantity);
        const subtotal = amount * quantity;
        sum += subtotal;
      }
    });

    return sum;
  }

  function handleConfirm() {
    navigate(PAYMENT_LINK_METHOD_PAGE, {
      state: {
        operationId: operationId,
        fromPaymentLinkPublicPage: true,
      },
    });
  }

  if (isLoading || !hasCheckedPaymentMethod) {
    return (
      <div className="flex w-full flex-col items-center justify-center py-12">
        <SpinnerComponent size={48} type="primary" />
        <p className="mt-4 text-neutral-600">Loading payment link...</p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col px-2">
      {/* Header Section - Merchant Info */}
      <div className="flex h-[242px] w-full flex-col items-center justify-start gap-4 self-stretch rounded-tl-2xl rounded-tr-2xl bg-red-600 py-6 md:px-28">
        <div className="flex flex-col items-center justify-end gap-2">
          <InitialsSquareComponent
            displayName={businessTradingName}
            size="md"
          />
          <div className="flex flex-col items-center justify-start gap-1">
            <span className="text-center text-xl font-medium leading-7 text-white">
              {businessTradingName}
            </span>
          </div>
        </div>
        <div className="flex h-14 flex-col items-center justify-start">
          <span className="text-base font-normal leading-normal text-rose-300">
            Amount
          </span>
          <span className="text-right text-2xl font-medium leading-loose text-white">
            {formatCurrency(totalAmount || 0)}
          </span>
        </div>
      </div>

      {/* Order Summary Section - Replaces the form */}
      <div className="flex h-auto w-full flex-col items-center justify-center gap-10 self-stretch rounded-bl-2xl rounded-br-2xl border-l border-r border-b border-stone-300 p-8">
        <div className="flex w-full flex-col">
          <h2 className="mb-4 text-xl font-medium text-neutral-900">
            Order Summary
          </h2>

          <div className="h-fit overflow-y-auto">
            {salesItems.map((item: SaleItemDTO, i: number) => (
              <div className="my-4" key={i}>
                <div className="mb-2 flex justify-between">
                  <p className="text-base font-normal text-neutral-900">
                    {item.description}
                  </p>
                  <p className="text-base font-medium text-neutral-900">
                    {formatCurrency(item.amountPerUnit)}
                  </p>
                </div>
                <div className="flex justify-between">
                  <p className="text-base font-normal text-neutral-900">
                    Quantity <b>{item.quantity}</b>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="my-3 mt-8 text-m font-medium text-black">
            Items ({getTotalItems()})
          </p>
          <hr className="border-stone-300" />
          <div className="my-4 flex justify-between pb-5">
            <p className="text-m font-medium text-black">Total</p>
            <h3 className="text-xl font-medium text-main-200-925">
              {formatCurrency(getTotalAmount())}
            </h3>
          </div>
          <hr className="my-4 h-px w-full border border-zinc-100" />

          <div className="mt-6 flex justify-center">
            <MajorButtonComponent
              id="confirm-payment-link-button"
              type="button"
              buttonType="primary"
              size="large"
              onClick={handleConfirm}
            >
              Confirm
            </MajorButtonComponent>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PaymentLinkPublicPage;
